import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { company } from "@/lib/company";

const UPDATED = "1 October 2026";

/**
 * Refunds and cancellations.
 *
 * Written against what the platform actually does, not what a generic policy
 * would claim: a cancellation refunds the original charge to the wallet (see
 * Service.refundShipment, which reverses by shipment id so a later price
 * change cannot refund the wrong amount), a card top-up is refunded to the
 * card through BobPay, and a shipment that has already been collected cannot
 * be cancelled at all because the state machine has no transition for it
 * (domain.StatusCollected).
 *
 * A starting draft, not a substitute for legal review -- the Consumer
 * Protection Act's cooling-off provisions and what counts as a service
 * already rendered are worth a lawyer's eye.
 */
export function Refunds() {
  return (
    <>
      <div className="mx-auto max-w-2xl px-4 py-12 text-ink-800">
        <Link to="/" className="text-sm font-medium text-brand">
          ← {company.tradingName}
        </Link>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink-900">
          Refunds &amp; cancellations
        </h1>
        <p className="mt-1 text-sm text-ink-400">Last updated {UPDATED}</p>

        <div className="prose-sm mt-8 space-y-6 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-ink-900">Cancelling a shipment</h2>
            <p className="mt-2">
              You can cancel any shipment that has not yet been collected, from the shipment page in
              the portal or the app. What you paid goes straight back to your WeShip wallet, as the
              amount actually charged rather than what the shipment would cost today, and the
              collection is taken off the driver's list.
            </p>
            <p className="mt-2">
              Once a parcel has been collected it cannot be cancelled — it is in a vehicle, and the
              work has been done. If you need it brought back, contact us and we will quote it as a
              return leg.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">When we cannot collect</h2>
            <p className="mt-2">
              If we fail to collect a parcel for a reason that is ours — no vehicle, a missed
              window, a service we should not have offered for that route — you are refunded in full
              to your wallet and we will tell you why.
            </p>
            <p className="mt-2">
              If we arrive and there is nothing to collect, nobody there, or the parcel is not ready,
              that attempt is chargeable: a vehicle and a driver went out. We will say so on the
              tracking timeline and arrange a second attempt.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Wallet balances</h2>
            <p className="mt-2">
              Money in your wallet stays yours. It does not expire. Refunds from a cancelled shipment
              are credited to the wallet rather than to your card, because that is where the charge
              came from and it is instant.
            </p>
            <p className="mt-2">
              If you want a wallet balance paid back out to the card or account it came from, email{" "}
              <a className="text-brand" href={`mailto:${company.billingEmail}`}>
                {company.billingEmail}
              </a>{" "}
              from the address on the account. We will reverse it through the original payment
              method where that is still possible, and by EFT where it is not. Allow a few working
              days for either to reflect — the delay is the bank's, not ours.
            </p>
            <p className="mt-2">
              Every movement in and out of your wallet is on your statement in the portal, including
              anything our own team adjusts by hand, which we also email you about at the time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Failed top-ups</h2>
            <p className="mt-2">
              If a card payment fails, nothing is added to your wallet and nothing is owed. Where a
              card was charged for a top-up that did not complete, the charge is reversed
              automatically. You will get an email either way.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Sending without an account</h2>
            <p className="mt-2">
              A guest booking is paid by card at checkout. If payment succeeds but the shipment
              cannot be booked, the charge is reversed in full the same day and we email you to say
              so — there is no wallet to credit and nothing for you to chase.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">Lost or damaged parcels</h2>
            <p className="mt-2">
              A claim for loss or damage is not a refund and is handled separately, against the
              declared value and the liability limits in the{" "}
              <Link className="text-brand" to="/terms">
                terms of service
              </Link>
              . Report it within seven days of the delivery date, or of the date delivery was due,
              with photographs of the packaging and the contents.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-ink-900">How to reach us</h2>
            <p className="mt-2">
              Billing and refunds:{" "}
              <a className="text-brand" href={`mailto:${company.billingEmail}`}>
                {company.billingEmail}
              </a>
              . Anything else:{" "}
              <a className="text-brand" href={`mailto:${company.supportEmail}`}>
                {company.supportEmail}
              </a>
              . Quote the waybill number — it is on every email we send you about a shipment.
            </p>
          </section>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
